import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { collectionMeta } from "../mock";
import { apiGetProducts } from "../api";

const Collection = () => {
  const { slug = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const genderParam = searchParams.get("gender");
  const [sort, setSort] = useState("featured");
  const [scentFilter, setScentFilter] = useState("all");
  const [base, setBase] = useState([]);
  const [loading, setLoading] = useState(true);

  const meta = collectionMeta[slug] || collectionMeta.all;

  useEffect(() => {
    setLoading(true);
    setScentFilter("all");
    const params = {};
    if (slug !== "all") params.category = slug;
    if (genderParam) params.gender = genderParam;
    apiGetProducts(params)
      .then(setBase)
      .catch(() => setBase([]))
      .finally(() => setLoading(false));
  }, [slug, genderParam]);

  const scents = useMemo(() => ["all", ...new Set(base.map((p) => p.scent))], [base]);

  const visible = useMemo(() => {
    let list = scentFilter === "all" ? base : base.filter((p) => p.scent === scentFilter);
    if (sort === "price-low") list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === "price-high") list = [...list].sort((a, b) => b.price - a.price);
    else if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [base, scentFilter, sort]);

  return (
    <div>
      <div className="bg-[#050304] py-14 text-center">
        <h1 className="font-serif text-4xl sm:text-5xl text-[#F3E7D8] capitalize">
          {meta.title}
          {genderParam ? ` — ${genderParam === "men" ? "For Him" : genderParam === "women" ? "For Her" : "Unisex"}` : ""}
        </h1>
        <p className="mt-3 text-[#E8B9C4] text-sm">{meta.subtitle}</p>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-2 flex-wrap">
            <SlidersHorizontal className="h-4 w-4 text-neutral-500" />
            {scents.map((s) => (
              <button
                key={s}
                onClick={() => setScentFilter(s)}
                className={`px-3.5 py-1.5 text-xs font-medium tracking-wide rounded-full border transition-all capitalize ${
                  scentFilter === s
                    ? "bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] border-transparent"
                    : "border-neutral-300 text-neutral-600 hover:border-[#C8932F]"
                }`}
              >
                {s === "all" ? "All Scents" : s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-500">{visible.length} products</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#C8932F] bg-white"
            >
              <option value="featured">Featured</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-neutral-200 rounded-sm" />
                <div className="h-4 bg-neutral-200 rounded mt-3 w-3/4" />
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <p className="text-center text-neutral-500 py-20">No products found in this collection.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {visible.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Collection;
